import { useQuery } from '@tanstack/react-query'
import { ChevronRight, Folder, FolderOpen } from 'lucide-react'
import { useState, type ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'

import { trpc } from '@maxus/website/lib'
import { cn } from '@maxus/website/utils'

import {
  Button,
  fieldTriggerClassName,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../base'

type NasPathPickerProps = Omit<
  ComponentProps<typeof Button>,
  'children' | 'disabled' | 'onClick' | 'type' | 'value' | 'variant'
> & {
  disabled: boolean
  projectId: string | null
  projectName: string
  storageId: string | null
  value: string | null
  onConfirm: (relativeStorageKey: string) => Promise<void>
}

type DirectoryNodeProps = {
  depth: number
  disabled: boolean
  expanded: boolean
  expandedPaths: Set<string>
  path: string
  projectId: string | null
  selectedPath: string
  storageId: string
  onExpandedChange: (path: string) => void
  onSelect: (path: string) => void
}

type DirectoryListProps = Omit<DirectoryNodeProps, 'expanded'>

const getChildPath = (parentPath: string, name: string): string => (parentPath ? `${parentPath}/${name}` : `/${name}`)

const normalizeNasPath = (path: string): string => `/${path.replace(/^\/+/, '')}`

const DirectoryList = ({
  depth,
  disabled,
  expandedPaths,
  path,
  projectId,
  selectedPath,
  storageId,
  onExpandedChange,
  onSelect,
}: DirectoryListProps) => {
  const { t } = useTranslation()
  const directoriesQuery = useQuery(
    trpc.project.listNasDirectories.queryOptions({
      localNasStorageId: storageId,
      projectId,
      relativeStorageKey: path,
    }),
  )
  const directories = directoriesQuery.data?.directories ?? []

  return (
    <div>
      {directoriesQuery.isPending && (
        <p className="px-2 py-1.5 text-xs text-text-tertiary" style={{ paddingLeft: `${depth * 16 + 24}px` }}>
          {t('Loading')}
        </p>
      )}
      {directoriesQuery.isError && (
        <p className="px-2 py-1.5 text-xs text-destructive" style={{ paddingLeft: `${depth * 16 + 24}px` }}>
          {t('Failed to load folders')}
        </p>
      )}
      {!directoriesQuery.isPending && !directoriesQuery.isError && directories.length === 0 && (
        <p className="px-2 py-1.5 text-xs text-text-tertiary" style={{ paddingLeft: `${depth * 16 + 24}px` }}>
          {t('No folders found')}
        </p>
      )}
      {directories.map(directory => {
        const childPath = getChildPath(path, directory.name)
        return (
          <DirectoryNode
            depth={depth}
            disabled={disabled}
            expanded={expandedPaths.has(childPath)}
            expandedPaths={expandedPaths}
            key={childPath}
            path={childPath}
            projectId={projectId}
            selectedPath={selectedPath}
            storageId={storageId}
            onExpandedChange={onExpandedChange}
            onSelect={onSelect}
          />
        )
      })}
    </div>
  )
}

const DirectoryNode = ({
  depth,
  disabled,
  expanded,
  expandedPaths,
  path,
  projectId,
  selectedPath,
  storageId,
  onExpandedChange,
  onSelect,
}: DirectoryNodeProps) => {
  const { t } = useTranslation()
  const label = path
    .split('/')
    .at(-1)!

  return (
    <div>
      <div className="flex min-w-0 items-center gap-1" style={{ paddingLeft: `${depth * 16}px` }}>
        <Button
          aria-label={expanded ? t('Collapse folder') : t('Expand folder')}
          className="size-6"
          disabled={disabled}
          onClick={() => onExpandedChange(path)}
          size="icon-xs"
          type="button"
          variant="ghost"
        >
          <ChevronRight className={cn('transition-transform', expanded && 'rotate-90')} />
        </Button>
        <Button
          aria-current={selectedPath === path ? 'true' : undefined}
          className="min-w-0 flex-1 justify-start px-1.5"
          disabled={disabled}
          onClick={() => onSelect(path)}
          type="button"
          variant="ghost"
        >
          {expanded ? (
            <FolderOpen className="size-4 shrink-0 text-primary" />
          ) : (
            <Folder className="size-4 shrink-0 text-primary" />
          )}
          <span className="truncate">{label}</span>
        </Button>
      </div>
      {expanded && (
        <DirectoryList
          depth={depth + 1}
          disabled={disabled}
          expandedPaths={expandedPaths}
          path={path}
          projectId={projectId}
          selectedPath={selectedPath}
          storageId={storageId}
          onExpandedChange={onExpandedChange}
          onSelect={onSelect}
        />
      )}
    </div>
  )
}

export const NasPathPicker = ({
  className,
  disabled,
  projectId,
  projectName,
  storageId,
  value,
  onConfirm,
  ...triggerProps
}: NasPathPickerProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [selectedPath, setSelectedPath] = useState(value ?? '')
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(() => new Set())
  const [isConfirming, setIsConfirming] = useState(false)

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (nextOpen) {
      setSelectedPath(value ?? '')
      setExpandedPaths(new Set())
    }
  }

  const handleExpandedChange = (path: string) => {
    setExpandedPaths(current => {
      const next = new Set(current)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }

  const handleConfirm = async () => {
    setIsConfirming(true)
    try {
      await onConfirm(normalizeNasPath(selectedPath))
      setOpen(false)
    } finally {
      setIsConfirming(false)
    }
  }

  return (
    <Popover onOpenChange={handleOpenChange} open={open}>
      <PopoverTrigger asChild>
        <Button
          {...triggerProps}
          aria-label={t('Browse NAS path for {{name}}', { name: projectName })}
          className={cn(fieldTriggerClassName, 'justify-start', className)}
          disabled={disabled || !storageId}
          title={value || undefined}
          type="button"
          variant="outline"
        >
          <Folder className="size-4 shrink-0" />
          <span className="truncate">{value || t('Select a NAS folder')}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[360px] p-2">
        {storageId && (
          <>
            <div className="max-h-72 overflow-auto py-1">
              <DirectoryList
                depth={0}
                disabled={disabled || isConfirming}
                expandedPaths={expandedPaths}
                path=""
                projectId={projectId}
                selectedPath={selectedPath}
                storageId={storageId}
                onExpandedChange={handleExpandedChange}
                onSelect={setSelectedPath}
              />
            </div>
            <div className="mt-2 flex items-center justify-between gap-2 border-t border-border-subtle pt-2">
              <p className="min-w-0 truncate text-xs text-text-tertiary">{selectedPath || t('Select a NAS folder')}</p>
              <Button
                disabled={disabled || isConfirming || !selectedPath}
                onClick={handleConfirm}
                size="sm"
                type="button"
              >
                {t('Save')}
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}
